import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  DescriptionOutlined as Description,
  SendOutlined as Send,
  DownloadOutlined as Download,
  EmailOutlined as Email,
  SmsOutlined as Sms,
  PreviewOutlined as Preview,
  EditOutlined as Edit,
  DeleteOutlined as Delete,
} from '@mui/icons-material';
import api from '../api';

const NoticeGenerator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [noticeType, setNoticeType] = useState('');
  const [noticeContent, setNoticeContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingLastNotice, setLoadingLastNotice] = useState(false);
  const [generatedNotices, setGeneratedNotices] = useState([]);
  const [expandedNoticeId, setExpandedNoticeId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingNotice, setDeletingNotice] = useState(null);

  const noticeTypes = [
    { value: 'late_rent', label: 'Late Rent Notice' },
    { value: 'lease_renewal', label: 'Lease Renewal Notice' },
    { value: 'inspection', label: 'Inspection Notice' },
    { value: 'pay_or_quit', label: 'Pay or Quit Notice' },
    { value: 'eviction', label: 'Eviction Notice' },
    { value: 'maintenance', label: 'Maintenance Notice' },
    { value: 'other', label: 'Other Notice' }
  ];

  useEffect(() => {
    fetchProperties();
    loadGeneratedNotices();
  }, []);

  useEffect(() => {
    // Skip if we're loading a previous notice (to avoid clearing data)
    if (loadingLastNotice) {
      return;
    }
    
    if (selectedProperty) {
      fetchUnits(selectedProperty);
      fetchTenants(selectedProperty);
    } else {
      setUnits([]);
      setTenants([]);
      setSelectedUnit('');
      setSelectedTenant('');
    }
  }, [selectedProperty, loadingLastNotice]);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      setProperties(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError('Failed to fetch properties');
    }
  };

  const fetchUnits = async (propertyId) => {
    try {
      console.log('Fetching units for property:', propertyId);
      
      // First, try to get units from the property object itself (they might be stored as property.units array)
      const property = properties.find(p => p.id === propertyId);
      if (property && property.units && Array.isArray(property.units) && property.units.length > 0) {
        console.log('Found units in property object:', property.units);
        // Units are stored on the property - use them directly
        const unitsData = property.units.map(unit => ({
          id: unit.id || unit.unitId || `unit-${Math.random()}`,
          name: unit.name || unit.unitNumber,
          unitNumber: unit.unitNumber,
          status: unit.status || unit.available ? 'available' : 'occupied',
          rent: unit.rent,
          ...unit
        }));
        console.log('Setting units from property object:', unitsData);
        setUnits(unitsData);
        return;
      }
      
      // If not found on property object, try the API endpoint
      const response = await api.get(`/api/properties/${propertyId}/units`);
      const unitsData = Array.isArray(response.data) ? response.data : [];
      console.log('Fetched units from API endpoint:', response.data);
      console.log('Setting units:', unitsData);
      setUnits(unitsData);
      
      if (unitsData.length === 0) {
        console.warn('No units found for property:', propertyId);
      }
    } catch (err) {
      console.error('Error fetching units:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to fetch units: ${err.response?.data?.message || err.message}`);
    }
  };

  const fetchTenants = async (propertyId) => {
    try {
      console.log('Fetching tenants for property:', propertyId);
      // Get all tenants from the general tenants collection
      const response = await api.get('/api/tenants');
      const allTenants = Array.isArray(response.data) ? response.data : [];
      console.log('All tenants from API:', allTenants);
      console.log('Total tenants found:', allTenants.length);
      
      // Filter tenants by propertyId if they have that field
      let tenantsData = allTenants.filter(t => {
        // If tenant has propertyId, match it
        if (t.propertyId) {
          return t.propertyId === propertyId;
        }
        // Otherwise include all (they might be assigned to units in this property)
        return true;
      });
      
      // If filtering by propertyId resulted in no tenants, show all tenants
      // (they might not have propertyId set but could still be relevant)
      if (tenantsData.length === 0 && allTenants.length > 0) {
        console.log('No tenants matched propertyId, showing all tenants');
        tenantsData = allTenants;
      }
      
      console.log('Filtered tenants for property:', propertyId, tenantsData);
      console.log('Setting tenants state with', tenantsData.length, 'tenants');
      setTenants(tenantsData);
      
      if (tenantsData.length === 0) {
        console.warn('No tenants found for property:', propertyId);
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to fetch tenants: ${err.response?.data?.message || err.message}`);
      setTenants([]); // Clear tenants on error
    }
  };

  const loadGeneratedNotices = async () => {
    setLoadingLastNotice(true);
    try {
      const response = await api.get('/api/notices');
      const notices = Array.isArray(response.data) ? response.data : [];
      setGeneratedNotices(notices);
      
      // Load the most recent notice into the form if available
      if (notices.length > 0) {
        const lastNotice = notices[0];
        setNoticeType(lastNotice.noticeType);
        
        if (lastNotice.propertyId) {
          await fetchUnits(lastNotice.propertyId);
          await fetchTenants(lastNotice.propertyId);
          await new Promise(resolve => setTimeout(resolve, 100));
          setSelectedProperty(lastNotice.propertyId);
          await new Promise(resolve => setTimeout(resolve, 200));
          if (lastNotice.unitId) {
            setSelectedUnit(lastNotice.unitId);
          }
          if (lastNotice.tenantId) {
            setSelectedTenant(lastNotice.tenantId);
          }
        }
      }
    } catch (err) {
      console.error('Error loading notices:', err);
      // Don't show error, just silently fail
    } finally {
      setLoadingLastNotice(false);
    }
  };

  const handleGenerate = async () => {
    if (!noticeType || !selectedProperty || !selectedUnit || !selectedTenant) {
      setError('Please fill in all required fields');
      return;
    }

    setIsGenerating(true);
    setError('');
    
    try {
      console.log('Generating notice with:', { noticeType, selectedProperty, selectedUnit, selectedTenant });
      
      // Use a longer timeout for AI generation (60 seconds)
      const response = await api.post('/api/notices/generate', {
        noticeType,
        propertyId: selectedProperty,
        unitId: selectedUnit,
        tenantId: selectedTenant
      }, {
        timeout: 60000 // 60 seconds for AI generation
      });
      
      console.log('Notice generated successfully:', response.data);
      setSuccess('Notice generated successfully!');
      // Refresh the list of generated notices
      await loadGeneratedNotices();
      // Expand the newly generated notice
      if (response.data.noticeId) {
        setExpandedNoticeId(response.data.noticeId);
      }
    } catch (err) {
      console.error('Error generating notice:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. The AI service may be slow. Please try again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to generate notice');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendEmail = async (notice) => {
    try {
      console.log('Sending email for notice:', notice);
      const response = await api.post('/api/notices/send', {
        noticeId: notice.id, // Include noticeId if available
        noticeType: notice.noticeType,
        propertyId: notice.propertyId,
        unitId: notice.unitId,
        tenantId: notice.tenantId,
        content: notice.content,
        method: 'email'
      });
      console.log('Email send response:', response.data);
      setSuccess(`Notice sent via email successfully to ${notice.tenantName || 'tenant'}!`);
      loadGeneratedNotices(); // Refresh list
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to send email';
      setError(`Failed to send email: ${errorMessage}`);
    }
  };

  const handleSendSMS = async (notice) => {
    try {
      await api.post('/api/notices/send', {
        noticeId: notice.id, // Include noticeId if available
        noticeType: notice.noticeType,
        propertyId: notice.propertyId,
        unitId: notice.unitId,
        tenantId: notice.tenantId,
        content: notice.content,
        method: 'sms'
      });
      setSuccess(`Notice sent via SMS successfully to ${notice.tenantName || 'tenant'}!`);
      loadGeneratedNotices(); // Refresh list
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Failed to send SMS');
    }
  };

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
    setEditedContent(notice.content);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingNotice || !editedContent.trim()) {
      setError('Notice content cannot be empty');
      return;
    }

    try {
      await api.put(`/api/notices/${editingNotice.id}`, { content: editedContent });
      setSuccess('Notice updated successfully!');
      setEditDialogOpen(false);
      setEditingNotice(null);
      setEditedContent('');
      await loadGeneratedNotices();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error updating notice:', err);
      setError(err.response?.data?.error || 'Failed to update notice');
    }
  };

  const handleDeleteNotice = (notice) => {
    setDeletingNotice(notice);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingNotice) return;

    try {
      await api.delete(`/api/notices/${deletingNotice.id}`);
      setSuccess('Notice deleted successfully!');
      setDeleteDialogOpen(false);
      setDeletingNotice(null);
      await loadGeneratedNotices();
      // Clear expanded notice if it was the deleted one
      if (expandedNoticeId === deletingNotice.id) {
        setExpandedNoticeId(null);
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error deleting notice:', err);
      setError(err.response?.data?.error || 'Failed to delete notice');
      setDeleteDialogOpen(false);
      setDeletingNotice(null);
    }
  };

  const handleDownloadPDF = async (notice) => {
    try {
      const response = await api.post('/api/notices/pdf', {
        noticeType: notice.noticeType,
        propertyId: notice.propertyId,
        unitId: notice.unitId,
        tenantId: notice.tenantId,
        content: notice.content
      }, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notice-${notice.noticeType}-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess('PDF downloaded successfully!');
    } catch (err) {
      setError('Failed to generate PDF');
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 1.5 } }}
      >
        Notice Generator
      </Typography>
      <Typography 
        variant="body2" 
        color="text.secondary" 
        paragraph
        sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' }, mb: { xs: 1.5, sm: 2 } }}
      >
        Generate legally formatted notices for tenants. AI-powered content with local tenancy law language.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: { xs: 1.5, sm: 2 } }}>
          {error}
        </Alert>
      )}

      {success && (
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
      )}

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
              >
                Notice Details
              </Typography>

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Notice Type</InputLabel>
                <Select
                  value={noticeType}
                  onChange={(e) => setNoticeType(e.target.value)}
                  label="Notice Type"
                >
                  {noticeTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Property</InputLabel>
                <Select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  label="Property"
                >
                  {properties.map((prop) => (
                    <MenuItem key={prop.id} value={prop.id}>
                      {prop.name} - {prop.address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={selectedUnit}
                  onChange={(e) => setSelectedUnit(e.target.value)}
                  label="Unit"
                  disabled={!selectedProperty}
                >
                  {units.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No units found for this property. Add units in the Properties & Units section.
                      </Typography>
                    </MenuItem>
                  ) : (
                    units.map((unit) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        {unit.name || unit.unitNumber || `Unit ${unit.id}`} - {unit.status || 'N/A'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tenant</InputLabel>
                <Select
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                  label="Tenant"
                  disabled={!selectedProperty}
                >
                  {tenants.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No tenants found. {tenants.length === 0 && selectedProperty ? 'Try selecting a different property.' : 'Select a property first.'}
                      </Typography>
                    </MenuItem>
                  ) : (
                    tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name || `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim() || 'Unknown'} - {tenant.email || 'No email'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<Description />}
                onClick={handleGenerate}
                disabled={isGenerating || !noticeType || !selectedProperty || !selectedUnit || !selectedTenant}
                fullWidth
                sx={{ mt: 2 }}
              >
                {isGenerating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : (
                  'Generate Notice'
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: { xs: 1.5, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography 
                  variant="h6"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Generated Notices
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadGeneratedNotices}
                  disabled={loadingLastNotice}
                  fullWidth={isMobile}
                >
                  Refresh
                </Button>
              </Box>

              {loadingLastNotice ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : generatedNotices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No notices generated yet. Generate your first notice using the form on the left.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {generatedNotices.map((notice) => (
                    <Paper
                      key={notice.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          cursor: 'pointer',
                          bgcolor: expandedNoticeId === notice.id ? 'action.selected' : 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' },
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1, sm: 0 }
                        }}
                        onClick={() => setExpandedNoticeId(expandedNoticeId === notice.id ? null : notice.id)}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="subtitle2" 
                            fontWeight="bold"
                            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                          >
                            {noticeTypes.find(t => t.value === notice.noticeType)?.label || notice.noticeType}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, display: 'block', mt: 0.5 }}
                          >
                            {notice.propertyName} - {notice.unitName} • {notice.tenantName}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ display: 'block', mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {new Date(notice.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 }, alignItems: 'center' }}>
                          <Chip
                            label={notice.status || 'draft'}
                            size="small"
                            color={notice.status === 'sent' ? 'success' : 'default'}
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          />
                          {expandedNoticeId === notice.id ? (
                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>▼</Typography>
                          ) : (
                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>▶</Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {expandedNoticeId === notice.id && (
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', maxHeight: { xs: 200, sm: 300 }, overflow: 'auto' }}>
                            <Typography 
                              variant="body2" 
                              style={{ whiteSpace: 'pre-wrap' }}
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              {notice.content}
                            </Typography>
                          </Paper>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            gap: { xs: 0.5, sm: 1 }, 
                            flexWrap: 'wrap',
                            flexDirection: { xs: 'column', sm: 'row' }
                          }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Preview />}
                              onClick={() => {
                                setNoticeContent(notice.content);
                                setIsPreviewOpen(true);
                              }}
                              fullWidth={isMobile}
                            >
                              Preview
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleEditNotice(notice)}
                              fullWidth={isMobile}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteNotice(notice)}
                              fullWidth={isMobile}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Email />}
                              onClick={() => handleSendEmail(notice)}
                              fullWidth={isMobile}
                            >
                              Send Email
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Sms />}
                              onClick={() => handleSendSMS(notice)}
                              fullWidth={isMobile}
                            >
                              Send SMS
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Download />}
                              onClick={() => handleDownloadPDF(notice)}
                              fullWidth={isMobile}
                            >
                              Download PDF
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog 
        open={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Notice Preview</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minHeight: { xs: 200, sm: 300 } }}>
            <Typography 
              variant="body1" 
              style={{ whiteSpace: 'pre-wrap' }}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {noticeContent || 'No content generated yet'}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setIsPreviewOpen(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Email />} 
            onClick={handleSendEmail}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Send Email
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Sms />} 
            onClick={handleSendSMS}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Send SMS
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Download />} 
            onClick={handleDownloadPDF}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingNotice(null);
          setEditedContent('');
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Edit Notice</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Notice Content"
            fullWidth
            multiline
            rows={isMobile ? 8 : 12}
            variant="outlined"
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            sx={{ mt: { xs: 0.5, sm: 1 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setEditingNotice(null);
              setEditedContent('');
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            color="primary"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingNotice(null);
        }}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Delete Notice</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to delete this notice? This action cannot be undone.
          </Typography>
          {deletingNotice && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
              >
                <strong>Notice Type:</strong> {deletingNotice.noticeType?.replace('_', ' ') || 'N/A'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: 0.5 }}
              >
                <strong>Tenant:</strong> {deletingNotice.tenantName || 'N/A'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                <strong>Unit:</strong> {deletingNotice.unitName || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletingNotice(null);
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoticeGenerator;






